import { useState, useRef, useCallback } from 'react';
import { Link, useParams } from 'wouter';
import {
  useGetClaim,
  useValidateClaim,
  useGetClaimExpenditure,
  customInstance,
} from '@edison/api-client-react';
import { ArrowLeft, Pencil, Check, X } from 'lucide-react';
import { normaliseFailureDetails } from './claim-failures.js';
import { AccessTokensPanel } from './access-tokens-panel.js';
import { ReviewersPanel } from './reviewers-panel.js';
import { RubricPanel } from './rubric-panel.js';
import { ClaimFindings } from './claim-findings.js';
import { ClaimReadinessTab } from './claim-readiness-tab.js';
import { ClaimExpenditureTab } from './claim-expenditure-tab.js';
import { ClaimSchedule1Tab } from './claim-schedule1-tab.js';
import { RdSharesTab } from './rd-shares-tab.js';
import { ClaimTimesheetTab } from './claim-timesheet-tab.js';
import { ActivityMapTab } from './activity-map-tab.js';
import { Badge } from '@/components/ui/badge.js';
import { Tabs } from '@/components/ui/tabs-ds.js';
import { SkeletonCard, SkeletonMetricTile } from '@/components/ui/skeleton.js';
import { MutationButton, type MutationState } from '@/components/ui/mutation-button.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { formatLabel, formatFy } from '@/lib/utils.js';

function formatCurrencyShort(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n)}`;
}

export interface AcknowledgementRequest {
  projectId: string;
  projectName: string;
  criterion: string;
  score: 'weak' | 'gap';
}

type ClaimTab = 'readiness' | 'expenditure' | 'timesheet' | 'schedule1' | 'access' | 'rubric' | 'shares' | 'activity-map';

const CLAIM_TABS = [
  { key: 'readiness',    label: 'Readiness' },
  { key: 'expenditure',  label: 'Expenditure' },
  { key: 'timesheet',    label: 'Timesheet' },
  { key: 'schedule1',    label: 'Schedule 1 output' },
  { key: 'access',       label: 'Auditor access' },
  { key: 'rubric',       label: 'Apportionment rubric' },
  { key: 'shares',       label: 'R&D shares' },
  { key: 'activity-map', label: 'Activity Map' },
] satisfies { key: ClaimTab; label: string }[];

export function ClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<ClaimTab>('readiness');
  const { data, isLoading, error, refetch } = useGetClaim(id);
  const { data: expenditureData } = useGetClaimExpenditure(id);

  // Inline claim-name edit
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const startEditName = useCallback((currentName: string) => {
    setNameValue(currentName);
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.select(), 0);
  }, []);
  const cancelEditName = useCallback(() => { setEditingName(false); setNameValue(''); }, []);
  const saveEditName = useCallback(async () => {
    if (!nameValue.trim()) return;
    setNameSaving(true);
    try {
      await customInstance({ url: `/api/claims/${id}`, method: 'PATCH', data: { name: nameValue.trim() } });
      setEditingName(false);
      void refetch();
    } finally {
      setNameSaving(false);
    }
  }, [id, nameValue, refetch]);

  const { mutate: validate, isPending: isValidating, error: validateError } = useValidateClaim();

  // Lock flow managed locally so we can handle the 422 ACKNOWLEDGEMENTS_REQUIRED step.
  const [lockState, setLockState] = useState<MutationState>('idle');
  const [lockApiError, setLockApiError] = useState<Error | null>(null);
  const [pendingAcks, setPendingAcks] = useState<AcknowledgementRequest[] | null>(null);
  const [ackTexts, setAckTexts] = useState<Record<string, string>>({});

  const handleLock = async () => {
    setLockState('saving');
    setLockApiError(null);
    try {
      const body = pendingAcks?.length
        ? {
            defensibilityAcknowledgements: pendingAcks.map((ack) => ({
              ...ack,
              text: ackTexts[`${ack.projectId}:${ack.criterion}`] ?? '',
            })),
          }
        : {};
      await customInstance({ url: `/api/claims/${id}/lock`, method: 'POST', data: body });
      setLockState('saved');
      setPendingAcks(null);
      setAckTexts({});
      void refetch();
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { error?: { code?: string; details?: AcknowledgementRequest[] } } } })?.response?.data?.error;
      if (errData?.code === 'ACKNOWLEDGEMENTS_REQUIRED' && errData.details?.length) {
        setPendingAcks(errData.details);
        setLockState('idle');
      } else {
        setLockApiError(err as Error);
        setLockState('error');
      }
    }
  };

  const claim = data?.data;
  const exp = expenditureData?.data as {
    labour?: { totalRdHours?: number; byProject?: Array<unknown> };
    totalExpenditure?: number;
  } | undefined;

  type RefusalError = { response?: { data?: { error?: { message?: string; details?: unknown } } } } | null | undefined;
  const validateRefusal = (validateError as RefusalError)?.response?.data?.error;
  const lockRefusal = (lockApiError as RefusalError)?.response?.data?.error;
  const actionFailures = [...normaliseFailureDetails(validateRefusal?.details), ...normaliseFailureDetails(lockRefusal?.details)];
  const actionMessage = actionFailures.length > 0 ? (lockRefusal?.message ?? validateRefusal?.message) : undefined;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <SkeletonMetricTile key={i} />)}
        </div>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error || !claim) {
    return <InlineError message="Failed to load claim." onRetry={() => void refetch()} />;
  }

  const projectCount = exp?.labour?.byProject?.length ?? 0;
  const totalExp = exp?.totalExpenditure ?? 0;
  const rdHours = exp?.labour?.totalRdHours ?? 0;
  // R&DTI refundable offset: 43.5% for entities with turnover < $20M
  const refundableOffset = totalExp * 0.435;

  return (
    <div className="space-y-6">
      {/* Back breadcrumb */}
      <Link href="/claims" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        All claims
      </Link>

      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                ref={nameInputRef}
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void saveEditName();
                  if (e.key === 'Escape') cancelEditName();
                }}
                className="text-2xl font-semibold tracking-tight bg-transparent border-b-2 border-primary outline-none w-72"
                disabled={nameSaving}
                aria-label="Claim name"
              />
              <button
                onClick={() => void saveEditName()}
                disabled={nameSaving}
                className="text-green-600 hover:text-green-700 disabled:opacity-50"
                aria-label="Save name"
              >
                <Check className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={cancelEditName}
                disabled={nameSaving}
                className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                aria-label="Cancel edit"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h1 className="text-2xl font-semibold tracking-tight">{claim.name}</h1>
              <button
                onClick={() => startEditName(claim.name)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                aria-label="Edit claim name"
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}
          <Badge
            variant={
              claim.status === 'locked'
                ? 'positive'
                : claim.status === 'validating'
                ? 'info'
                : 'default'
            }
          >
            {formatLabel(claim.status)}
          </Badge>
          {(claim.status === 'validating' || !!pendingAcks?.length) && (
            <MutationButton
              state={lockState}
              idleLabel={pendingAcks?.length ? 'Confirm lock' : 'Lock claim'}
              savingLabel="Locking…"
              savedLabel="Locked"
              onClick={() => void handleLock()}
              variant="primary"
              size="sm"
            />
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {formatFy(claim.incomeYear)}
          {projectCount > 0 && <span className="ml-2">· {projectCount} project{projectCount !== 1 ? 's' : ''}</span>}
          {rdHours > 0 && <span className="ml-2">· {rdHours.toFixed(1)}h R&D</span>}
          {totalExp > 0 && <span className="ml-2">· {formatCurrencyShort(totalExp)} eligible expenditure</span>}
          {totalExp > 0 && <span className="ml-2">· est. {formatCurrencyShort(refundableOffset)} refundable offset</span>}
          {claim.lockedAt && (
            <span className="ml-2">· Locked {new Date(claim.lockedAt).toLocaleDateString('en-AU')}</span>
          )}
        </p>
      </div>

      {/* Tab navigation */}
      <Tabs
        tabs={CLAIM_TABS}
        active={activeTab}
        onSelect={(k) => setActiveTab(k as ClaimTab)}
      />

      {/* ── Readiness tab ── */}
      {activeTab === 'readiness' && (
        <ClaimReadinessTab
          claimId={id}
          claimStatus={claim.status}
          onValidate={() => validate({ id }, { onSuccess: () => void refetch() })}
          onLock={handleLock}
          isValidating={isValidating}
          isLocking={lockState === 'saving'}
          validationFailures={actionFailures}
          validationMessage={actionMessage}
          pendingAcks={pendingAcks}
          ackTexts={ackTexts}
          onAckChange={(key, text) => setAckTexts((prev) => ({ ...prev, [key]: text }))}
        />
      )}

      {/* ── Expenditure tab ── */}
      {activeTab === 'expenditure' && (
        <ClaimExpenditureTab claimId={id} claimStatus={claim.status} claimMode={(claim as unknown as Record<string, unknown>)['claimMode'] as string | null | undefined} onModeChange={() => void refetch()} />
      )}

      {/* ── Timesheet tab (Stage 1 apportionment evidence store, Task 13) ── */}
      {activeTab === 'timesheet' && (
        <ClaimTimesheetTab claimId={id} incomeYear={claim.incomeYear} />
      )}

      {/* ── Schedule 1 tab ── */}
      {activeTab === 'schedule1' && (
        <ClaimSchedule1Tab claimId={id} claimStatus={claim.status} />
      )}

      {/* ── Auditor Access tab ── */}
      {activeTab === 'access' && (
        <div className="space-y-8">
          {/* Authenticated reviewer grants (Group B) — self-gates to tenant_admin/r_and_d_manager. */}
          <ReviewersPanel claimId={id} />
          <AccessTokensPanel claimId={id} claimStatus={claim.status} />
          {/* ERA-020: surface findings raised by auditors/advisors so they're actioned, not lost. */}
          <ClaimFindings claimId={id} />
        </div>
      )}

      {/* ── Apportionment rubric tab ── */}
      {/* The rubric is tenant-wide (not claim-scoped) — later apportionment tasks (commit
          classification, per-project R&D share) will add their own tabs alongside this one. */}
      {activeTab === 'rubric' && <RubricPanel />}

      {/* ── R&D shares tab (Stage 1 apportionment evidence store, Task 12) ── */}
      {activeTab === 'shares' && <RdSharesTab claimId={id} incomeYear={claim.incomeYear} />}

      {/* ── Activity Map tab (Stage 1 apportionment evidence store, Task 17) ── */}
      {activeTab === 'activity-map' && <ActivityMapTab claimId={id} incomeYear={claim.incomeYear} />}
    </div>
  );
}
